/**
 * Studies Modal Component V3
 *
 * Modal for creating and editing studies with TIME-WINDOWED clinic and device associations.
 * Supports creating associations with start_timestamp and end_timestamp for both clinics and devices.
 */

import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Badge, Card, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RefreshButton from '@/components/common/RefreshButton';
import TimeWindowTimeline from '@/components/common/TimeWindowTimeline';
import type { Study, Clinic, Device } from '@/types/associations';

interface ClinicAssociation {
  clinic_id: number;
  start_timestamp?: string;
  end_timestamp?: string;
}

interface DeviceAssociation {
  device_id: number;
  start_timestamp?: string;
  end_timestamp?: string;
}

interface StudiesModalProps {
  show: boolean;
  study?: Study | null;
  clinics?: Clinic[];
  devices?: Device[];
  onHide: () => void;
  onSave: (data: Partial<Study>) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export default function StudiesModalV3({
  show,
  study,
  clinics = [],
  devices = [],
  onHide,
  onSave,
  onRefresh,
}: StudiesModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    study_id: '',
    description: '',
    principal_investigator: '',
    start_date: '',
    end_date: '',
  });

  const [clinicAssociations, setClinicAssociations] = useState<ClinicAssociation[]>([]);
  const [deviceAssociations, setDeviceAssociations] = useState<DeviceAssociation[]>([]);

  const [selectedClinicId, setSelectedClinicId] = useState<string>('');
  const [newClinicAssociation, setNewClinicAssociation] = useState({
    start_timestamp: '',
    end_timestamp: '',
  });

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [newDeviceAssociation, setNewDeviceAssociation] = useState({
    start_timestamp: '',
    end_timestamp: '',
  });

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (study) {
      setFormData({
        name: study.name || '',
        study_id: study.study_id || '',
        description: study.description || '',
        principal_investigator: study.principal_investigator || '',
        start_date: study.start_date ? study.start_date.substring(0, 10) : '',
        end_date: study.end_date ? study.end_date.substring(0, 10) : '',
      });
      // Load existing associations if available
      setClinicAssociations([]);
      setDeviceAssociations([]);
    } else {
      setFormData({
        name: '',
        study_id: '',
        description: '',
        principal_investigator: '',
        start_date: '',
        end_date: '',
      });
      setClinicAssociations([]);
      setDeviceAssociations([]);
    }
    setSelectedClinicId('');
    setSelectedDeviceId('');
    setNewClinicAssociation({ start_timestamp: '', end_timestamp: '' });
    setNewDeviceAssociation({ start_timestamp: '', end_timestamp: '' });
    setError(null);
  }, [study, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate dates
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      setError(t('admin.policyDatasets.endMustBeAfterStart'));
      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        ...formData,
        clinic_associations: clinicAssociations,
        device_associations: deviceAssociations,
      } as Partial<Study>);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.policyDatasets.saveFailed', { type: 'study' }));
    } finally {
      setIsSaving(false);
    }
  };

  // Clinic Association Handlers
  const handleAddClinicAssociation = () => {
    if (!selectedClinicId) {
      setError(t('admin.policyDatasets.selectClinicFirst'));
      return;
    }

    const clinicId = parseInt(selectedClinicId, 10);

    if (clinicAssociations.some((a) => a.clinic_id === clinicId)) {
      setError(t('admin.policyDatasets.clinicAlreadyAssociated'));
      return;
    }

    if (newClinicAssociation.start_timestamp && newClinicAssociation.end_timestamp) {
      const start = new Date(newClinicAssociation.start_timestamp);
      const end = new Date(newClinicAssociation.end_timestamp);
      if (start >= end) {
        setError(t('admin.policyDatasets.endMustBeAfterStart'));
        return;
      }
    }

    const association: ClinicAssociation = {
      clinic_id: clinicId,
      start_timestamp: newClinicAssociation.start_timestamp || undefined,
      end_timestamp: newClinicAssociation.end_timestamp || undefined,
    };

    setClinicAssociations([...clinicAssociations, association]);
    setSelectedClinicId('');
    setNewClinicAssociation({ start_timestamp: '', end_timestamp: '' });
    setError(null);
  };

  const handleRemoveClinicAssociation = (clinicId: number) => {
    setClinicAssociations(clinicAssociations.filter((a) => a.clinic_id !== clinicId));
  };

  // Device Association Handlers
  const handleAddDeviceAssociation = () => {
    if (!selectedDeviceId) {
      setError(t('admin.policyDatasets.selectDeviceFirst'));
      return;
    }

    const deviceId = parseInt(selectedDeviceId, 10);

    if (deviceAssociations.some((a) => a.device_id === deviceId)) {
      setError(t('admin.policyDatasets.deviceAlreadyAssociated'));
      return;
    }

    if (newDeviceAssociation.start_timestamp && newDeviceAssociation.end_timestamp) {
      const start = new Date(newDeviceAssociation.start_timestamp);
      const end = new Date(newDeviceAssociation.end_timestamp);
      if (start >= end) {
        setError(t('admin.policyDatasets.endMustBeAfterStart'));
        return;
      }
    }

    const association: DeviceAssociation = {
      device_id: deviceId,
      start_timestamp: newDeviceAssociation.start_timestamp || undefined,
      end_timestamp: newDeviceAssociation.end_timestamp || undefined,
    };

    setDeviceAssociations([...deviceAssociations, association]);
    setSelectedDeviceId('');
    setNewDeviceAssociation({ start_timestamp: '', end_timestamp: '' });
    setError(null);
  };

  const handleRemoveDeviceAssociation = (deviceId: number) => {
    setDeviceAssociations(deviceAssociations.filter((a) => a.device_id !== deviceId));
  };

  // Helper Functions
  const getClinicName = (clinicId: number): string => {
    const clinic = clinics.find((c) => c.id === clinicId);
    return clinic ? (clinic.name || clinic.clinic_id) + ` (${clinic.clinic_id})` : `Clinic ${clinicId}`;
  };

  const getDeviceName = (deviceId: number): string => {
    const device = devices.find((d) => d.id === deviceId);
    return device ? device.device_id + (device.description ? ` (${device.description})` : '') : `Device ${deviceId}`;
  };

  // Timeline Data
  const clinicTimelineWindows = useMemo(() => {
    const studyId = study?.id;
    return clinicAssociations
      .filter((a) => a.start_timestamp && a.end_timestamp)
      .map((a) => ({
        id: a.clinic_id,
        label: getClinicName(a.clinic_id),
        start: new Date(a.start_timestamp!),
        end: new Date(a.end_timestamp!),
        entity1: studyId, // study_id (all same, so overlap detection will work on entity2)
        entity2: a.clinic_id, // clinic_id - overlaps only when same clinic has multiple windows
      }));
  }, [clinicAssociations, clinics, study]);

  const deviceTimelineWindows = useMemo(() => {
    const studyId = study?.id;
    return deviceAssociations
      .filter((a) => a.start_timestamp && a.end_timestamp)
      .map((a) => ({
        id: a.device_id,
        label: getDeviceName(a.device_id),
        start: new Date(a.start_timestamp!),
        end: new Date(a.end_timestamp!),
        entity1: studyId, // study_id (all same, so overlap detection will work on entity2)
        entity2: a.device_id, // device_id - overlaps only when same device has multiple windows
      }));
  }, [deviceAssociations, devices, study]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>{study ? t('admin.policyDatasets.editStudy') : t('admin.policyDatasets.createStudy')}</Modal.Title>
          {onRefresh && <RefreshButton onRefresh={onRefresh} size="sm" variant="outline-secondary" />}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form id="studies-form-v3" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <h6 className="mb-3">{t('admin.policyDatasets.basicInformation')}</h6>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('admin.policyDatasets.namePlaceholder', { example: 'Cardiac Study 2025' })}
              required
              autoFocus
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.nameRequired', { type: 'study' })}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.studyId')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.study_id}
              onChange={(e) => setFormData({ ...formData, study_id: e.target.value })}
              placeholder={t('admin.policyDatasets.studyIdPlaceholder')}
              required
              disabled={!!study}
            />
            <Form.Text className="text-muted">
              {study ? t('admin.policyDatasets.studyIdCannotChange') : t('admin.policyDatasets.studyIdRequired')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyDatasets.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('admin.policyDatasets.descriptionPlaceholder')}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyDatasets.principalInvestigator')}</Form.Label>
            <Form.Control
              type="text"
              value={formData.principal_investigator}
              onChange={(e) => setFormData({ ...formData, principal_investigator: e.target.value })}
              placeholder={t('admin.policyDatasets.principalInvestigatorPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.principalInvestigatorOptional')}</Form.Text>
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('admin.policyDatasets.startDate')}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
                <Form.Text className="text-muted">{t('admin.policyDatasets.startDateOptional')}</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('admin.policyDatasets.endDate')}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
                <Form.Text className="text-muted">{t('admin.policyDatasets.endDateOptional')}</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          {/* Associations with Tabs */}
          <h6 className="mb-3">Time-Windowed Associations</h6>

          <Tabs defaultActiveKey="clinics" className="mb-3">
            {/* Clinic Associations Tab */}
            <Tab
              eventKey="clinics"
              title={
                <>
                  {t('admin.policyDatasets.clinics')} <Badge bg="secondary">{clinicAssociations.length}</Badge>
                </>
              }
            >
              {clinics.length === 0 ? (
                <Alert variant="info">{t('admin.policyDatasets.noClinicsAvailable')}</Alert>
              ) : (
                <>
                  {/* Add Clinic Association Form */}
                  <Card className="mb-3 bg-light">
                    <Card.Body>
                      <h6 className="mb-3">{t('admin.policyDatasets.addClinicAssociation')}</h6>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.clinics')}</Form.Label>
                            <Form.Select
                              value={selectedClinicId}
                              onChange={(e) => setSelectedClinicId(e.target.value)}
                            >
                              <option value="">{t('admin.policyDatasets.selectClinic')}</option>
                              {clinics
                                .filter((c) => !clinicAssociations.some((a) => a.clinic_id === c.id))
                                .map((clinic) => (
                                  <option key={clinic.id} value={clinic.id}>
                                    {clinic.name || clinic.clinic_id}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.startTimestamp')}</Form.Label>
                            <Form.Control
                              type="datetime-local"
                              value={newClinicAssociation.start_timestamp}
                              onChange={(e) =>
                                setNewClinicAssociation({ ...newClinicAssociation, start_timestamp: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.endTimestamp')}</Form.Label>
                            <Form.Control
                              type="datetime-local"
                              value={newClinicAssociation.end_timestamp}
                              onChange={(e) =>
                                setNewClinicAssociation({ ...newClinicAssociation, end_timestamp: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="d-block">&nbsp;</Form.Label>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleAddClinicAssociation}
                            disabled={!selectedClinicId}
                            className="w-100"
                          >
                            {t('admin.policyDatasets.add')}
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Clinic Timeline */}
                  {clinicTimelineWindows.length > 0 && (
                    <div className="mb-3">
                      <h6 className="mb-2">{t('admin.policyDatasets.timeWindowVisualization')}</h6>
                      <TimeWindowTimeline windows={clinicTimelineWindows} highlightOverlaps={true} height={150} />
                    </div>
                  )}

                  {/* Clinic Associations List */}
                  {clinicAssociations.length > 0 ? (
                    clinicAssociations.map((assoc) => (
                      <Card key={assoc.clinic_id} className="mb-2">
                        <Card.Body className="py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{getClinicName(assoc.clinic_id)}</strong>
                              {assoc.start_timestamp || assoc.end_timestamp ? (
                                <div className="text-muted small mt-1">
                                  {assoc.start_timestamp ? (
                                    <span>{t('admin.policyDatasets.from')}: {new Date(assoc.start_timestamp).toLocaleString()}</span>
                                  ) : (
                                    <span>{t('admin.policyDatasets.from')}: {t('admin.policyDatasets.unlimited')}</span>
                                  )}
                                  {' → '}
                                  {assoc.end_timestamp ? (
                                    <span>{t('admin.policyDatasets.to')}: {new Date(assoc.end_timestamp).toLocaleString()}</span>
                                  ) : (
                                    <span>{t('admin.policyDatasets.to')}: {t('admin.policyDatasets.unlimited')}</span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-muted small mt-1">{t('admin.policyDatasets.noTimeWindow')}</div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveClinicAssociation(assoc.clinic_id)}
                            >
                              {t('admin.policyDatasets.remove')}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <Alert variant="info">{t('admin.policyDatasets.noClinicAssociations')}</Alert>
                  )}
                </>
              )}
            </Tab>

            {/* Device Associations Tab */}
            <Tab
              eventKey="devices"
              title={
                <>
                  {t('admin.policyDatasets.devices')} <Badge bg="secondary">{deviceAssociations.length}</Badge>
                </>
              }
            >
              {devices.length === 0 ? (
                <Alert variant="info">{t('admin.policyDatasets.noDevicesAvailable')}</Alert>
              ) : (
                <>
                  {/* Add Device Association Form */}
                  <Card className="mb-3 bg-light">
                    <Card.Body>
                      <h6 className="mb-3">{t('admin.policyDatasets.addDeviceAssociation')}</h6>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.devices')}</Form.Label>
                            <Form.Select
                              value={selectedDeviceId}
                              onChange={(e) => setSelectedDeviceId(e.target.value)}
                            >
                              <option value="">{t('admin.policyDatasets.selectDevice')}</option>
                              {devices
                                .filter((d) => !deviceAssociations.some((a) => a.device_id === d.id))
                                .map((device) => (
                                  <option key={device.id} value={device.id}>
                                    {device.device_id} {device.description && `(${device.description})`}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.startTimestamp')}</Form.Label>
                            <Form.Control
                              type="datetime-local"
                              value={newDeviceAssociation.start_timestamp}
                              onChange={(e) =>
                                setNewDeviceAssociation({ ...newDeviceAssociation, start_timestamp: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t('admin.policyDatasets.endTimestamp')}</Form.Label>
                            <Form.Control
                              type="datetime-local"
                              value={newDeviceAssociation.end_timestamp}
                              onChange={(e) =>
                                setNewDeviceAssociation({ ...newDeviceAssociation, end_timestamp: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="d-block">&nbsp;</Form.Label>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleAddDeviceAssociation}
                            disabled={!selectedDeviceId}
                            className="w-100"
                          >
                            {t('admin.policyDatasets.add')}
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Device Timeline */}
                  {deviceTimelineWindows.length > 0 && (
                    <div className="mb-3">
                      <h6 className="mb-2">{t('admin.policyDatasets.timeWindowVisualization')}</h6>
                      <TimeWindowTimeline windows={deviceTimelineWindows} highlightOverlaps={true} height={150} />
                    </div>
                  )}

                  {/* Device Associations List */}
                  {deviceAssociations.length > 0 ? (
                    deviceAssociations.map((assoc) => (
                      <Card key={assoc.device_id} className="mb-2">
                        <Card.Body className="py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{getDeviceName(assoc.device_id)}</strong>
                              {assoc.start_timestamp || assoc.end_timestamp ? (
                                <div className="text-muted small mt-1">
                                  {assoc.start_timestamp ? (
                                    <span>{t('admin.policyDatasets.from')}: {new Date(assoc.start_timestamp).toLocaleString()}</span>
                                  ) : (
                                    <span>{t('admin.policyDatasets.from')}: {t('admin.policyDatasets.unlimited')}</span>
                                  )}
                                  {' → '}
                                  {assoc.end_timestamp ? (
                                    <span>{t('admin.policyDatasets.to')}: {new Date(assoc.end_timestamp).toLocaleString()}</span>
                                  ) : (
                                    <span>{t('admin.policyDatasets.to')}: {t('admin.policyDatasets.unlimited')}</span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-muted small mt-1">{t('admin.policyDatasets.noTimeWindow')}</div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveDeviceAssociation(assoc.device_id)}
                            >
                              {t('admin.policyDatasets.remove')}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <Alert variant="info">{t('admin.policyDatasets.noDeviceAssociations')}</Alert>
                  )}
                </>
              )}
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="studies-form-v3" disabled={isSaving}>
          {isSaving ? t('admin.policyDatasets.saving') : study ? t('common.update') : t('common.create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
